import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, TrendingUp, DollarSign, Search, MoreVertical, Eye, Download, MessageSquare, Phone, RefreshCw, X, Plus, FileText } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import AdminBookingModal from './components/AdminBookingModal';
import { generateAppointmentReceipt } from '../../../utils/pdfReceiptGenerator';



const AdminAppointmentDashboard = () => {
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errors, setErrors] = useState({});

  // Dashboard Stats
  const [dashboardStats, setDashboardStats] = useState({
    totalAppointments: 0,
    todayAppointments: 0,
    weeklyRevenue: 0,
    completionRate: 0
  });

  // Filters and Search
  const [filters, setFilters] = useState({
    status: 'all',
    dateRange: 'today',
    treatmentId: 'all',
    doctorId: 'all',
    consultationMode: 'all',
    paymentStatus: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date'); // date, fee, status, payment

  // Calendar and View
  const [viewMode, setViewMode] = useState('list'); // list, calendar, analytics
  const [selectedDate, setSelectedDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [appointments, filters, searchTerm, sortBy]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAppointments(),
        fetchTreatments(),
        fetchDoctors()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointments = async () => {
    try {
      const { data, error } = await supabase
        ?.from('appointments')
        ?.select(`
          id, status, severity, fee, consultation_mode,
          preferred_date, preferred_time, scheduled_datetime,
          chief_complaint, medical_history, notes, created_at,
          booking_type, service_ids,
          client:clients!client_id(full_name, email, phone),
          doctor:user_profiles!doctor_id(full_name, email),
          treatment:treatments(title, short_description, expected_duration)
        `)
        ?.order('created_at', { ascending: false });

      if (error) {
        setErrors({ appointments: 'Failed to load appointments' });
        return;
      }

      setAppointments(data || []);
      calculateDashboardStats(data || []);
    } catch (error) {
      setErrors({ appointments: 'Cannot connect to appointments database' });
    }
  };

  const fetchTreatments = async () => {
    try {
      const { data, error } = await supabase
        ?.from('treatments')
        ?.select('id, title')
        ?.eq('status', 'published');

      if (error) {
        setErrors({ treatments: 'Failed to load treatments' });
        return;
      }

      setTreatments(data || []);
    } catch (error) {
      setErrors({ treatments: 'Cannot connect to treatments database' });
    }
  };

  const fetchDoctors = async () => {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name')
        ?.eq('role', 'doctor')
        ?.eq('is_active', true);

      if (error) {
        setErrors({ doctors: 'Failed to load doctors' });
        return;
      }

      setDoctors(data || []);
    } catch (error) {
      setErrors({ doctors: 'Cannot connect to doctors database' });
    }
  };

  const calculateDashboardStats = (appointmentsData) => {
    const today = new Date()?.toISOString()?.split('T')?.[0];
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];

    const todayAppointments = appointmentsData?.filter(apt =>
      apt?.preferred_date === today
    )?.length || 0;

    const weeklyRevenue = appointmentsData
      ?.filter(apt => apt?.preferred_date >= weekAgo)
      ?.reduce((sum, apt) => sum + (parseFloat(apt?.fee) || 0), 0) || 0;

    const completedAppointments = appointmentsData?.filter(apt =>
      apt?.status === 'completed'
    )?.length || 0;

    const completionRate = appointmentsData?.length > 0
      ? Math.round((completedAppointments / appointmentsData?.length) * 100)
      : 0;

    setDashboardStats({
      totalAppointments: appointmentsData?.length || 0,
      todayAppointments,
      weeklyRevenue,
      completionRate
    });
  };

  const applyFilters = () => {
    let filtered = [...appointments];

    // Status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(apt => apt?.status === filters?.status);
    }

    // Date range filter
    const today = new Date()?.toISOString()?.split('T')?.[0];
    const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
    const weekFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];

    switch (filters?.dateRange) {
      case 'today':
        filtered = filtered?.filter(apt => apt?.preferred_date === today);
        break;
      case 'tomorrow':
        filtered = filtered?.filter(apt => apt?.preferred_date === tomorrow);
        break;
      case 'week':
        filtered = filtered?.filter(apt => apt?.preferred_date <= weekFromNow);
        break;
      default:
        break;
    }

    // Treatment filter
    if (filters?.treatmentId !== 'all') {
      filtered = filtered?.filter(apt => apt?.treatment?.id === filters?.treatmentId);
    }

    // Doctor filter
    if (filters?.doctorId !== 'all') {
      filtered = filtered?.filter(apt => apt?.doctor?.id === filters?.doctorId);
    }

    // Consultation mode filter
    if (filters?.consultationMode !== 'all') {
      filtered = filtered?.filter(apt => apt?.consultation_mode === filters?.consultationMode);
    }

    // Payment status filter
    if (filters?.paymentStatus !== 'all') {
      filtered = filtered?.filter(apt => apt?.payment_status === filters?.paymentStatus);
    }

    // Search filter
    if (searchTerm?.trim()) {
      const term = searchTerm?.toLowerCase();
      filtered = filtered?.filter(apt =>
        apt?.patient?.full_name?.toLowerCase()?.includes(term) ||
        apt?.patient?.email?.toLowerCase()?.includes(term) ||
        apt?.chief_complaint?.toLowerCase()?.includes(term) ||
        apt?.treatment?.title?.toLowerCase()?.includes(term)
      );
    }

    // Sorting
    switch (sortBy) {
      case 'date':
        filtered = filtered?.sort((a, b) => {
          const dateA = new Date(`${a?.preferred_date} ${a?.preferred_time}`);
          const dateB = new Date(`${b?.preferred_date} ${b?.preferred_time}`);
          return dateB - dateA; // Most recent first
        });
        break;
      case 'fee':
        filtered = filtered?.sort((a, b) => (b?.fee || 0) - (a?.fee || 0)); // Highest first
        break;
      case 'status':
        filtered = filtered?.sort((a, b) => (a?.status || '')?.localeCompare(b?.status || ''));
        break;
      case 'payment':
        filtered = filtered?.sort((a, b) => (a?.payment_status || '')?.localeCompare(b?.payment_status || ''));
        break;
      default:
        break;
    }

    setFilteredAppointments(filtered);
  };

  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      const { error } = await supabase
        ?.from('appointments')
        ?.update({
          status: newStatus,
          completed_at: newStatus === 'completed' ? new Date()?.toISOString() : null
        })
        ?.eq('id', appointmentId);

      if (error) {
        setErrors({ update: 'Failed to update appointment status' });
        return;
      }

      await fetchAppointments();
      setSelectedAppointment(null);
    } catch (error) {
      setErrors({ update: 'Cannot connect to update appointment' });
    }
  };

  const updatePaymentStatus = async (appointmentId, paymentStatus, paymentMethod) => {
    try {
      const { error } = await supabase
        ?.from('appointments')
        ?.update({
          payment_status: paymentStatus,
          payment_method: paymentMethod
        })
        ?.eq('id', appointmentId);

      if (error) {
        setErrors({ update: 'Failed to update payment status' });
        return;
      }

      await fetchAppointments();
      // Update the selected appointment to reflect changes
      const updatedAppointment = appointments?.find(apt => apt?.id === appointmentId);
      if (updatedAppointment) {
        setSelectedAppointment({
          ...updatedAppointment,
          payment_status: paymentStatus,
          payment_method: paymentMethod
        });
      }
    } catch (error) {
      setErrors({ update: 'Cannot connect to update payment' });
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  const DashboardMetrics = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {[
        {
          title: 'Total Appointments',
          value: dashboardStats?.totalAppointments,
          icon: Calendar,
          color: 'blue',
          change: '+12%'
        },
        {
          title: 'Today\'s Appointments',
          value: dashboardStats?.todayAppointments,
          icon: Clock,
          color: 'emerald',
          change: '+5%'
        },
        {
          title: 'Weekly Revenue',
          value: `₹${dashboardStats?.weeklyRevenue?.toLocaleString()}`,
          icon: DollarSign,
          color: 'purple',
          change: '+18%'
        },
        {
          title: 'Completion Rate',
          value: `${dashboardStats?.completionRate}%`,
          icon: TrendingUp,
          color: 'amber',
          change: '+3%'
        }
      ]?.map((metric, index) => {
        const Icon = metric?.icon;
        return (
          <motion.div
            key={metric?.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/10 backdrop-blur-lg border border-gray-600 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 bg-${metric?.color}-500/20 rounded-lg flex items-center justify-center`}>
                <Icon className={`w-6 h-6 text-${metric?.color}-400`} />
              </div>
              <span className="text-emerald-400 text-sm font-medium">{metric?.change}</span>
            </div>
            <h3 className="text-2xl font-bold text-white mb-1">{metric?.value}</h3>
            <p className="text-gray-400 text-sm">{metric?.title}</p>
          </motion.div>
        );
      })}
    </div>
  );

  const FilterControls = () => (
    <div className="bg-white/10 backdrop-blur-lg border border-gray-600 rounded-xl p-6 mb-6">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-64">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              placeholder="Search appointments..."
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <select
          value={filters?.status}
          onChange={(e) => setFilters(prev => ({ ...prev, status: e?.target?.value }))}
          className="px-4 py-2 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          <option value="scheduled">Scheduled</option>
          <option value="confirmed">Confirmed</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <select
          value={filters?.dateRange}
          onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e?.target?.value }))}
          className="px-4 py-2 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Dates</option>
          <option value="today">Today</option>
          <option value="tomorrow">Tomorrow</option>
          <option value="week">This Week</option>
        </select>

        <select
          value={filters?.consultationMode}
          onChange={(e) => setFilters(prev => ({ ...prev, consultationMode: e?.target?.value }))}
          className="px-4 py-2 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Modes</option>
          <option value="in_person">In Person</option>
          <option value="video_call">Video Call</option>
          <option value="phone_call">Phone Call</option>
        </select>

        <select
          value={filters?.paymentStatus}
          onChange={(e) => setFilters(prev => ({ ...prev, paymentStatus: e?.target?.value }))}
          className="px-4 py-2 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Payments</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="refunded">Refunded</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e?.target?.value)}
          className="px-4 py-2 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="date">Sort by Date</option>
          <option value="fee">Sort by Fee</option>
          <option value="status">Sort by Status</option>
          <option value="payment">Sort by Payment</option>
        </select>

        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>
    </div>
  );

  const AppointmentCard = ({ appointment }) => {
    // Determine booking type display
    const getBookingTypeDisplay = () => {
      if (appointment?.booking_type === 'consultation') {
        return 'Consultation';
      } else if (appointment?.booking_type === 'service') {
        // Show service names if available
        // service_ids is JSONB array: [{"service_id": "uuid", "name": "Service Name", "base_price": 1000, "category": "category"}]
        if (appointment?.service_ids && Array.isArray(appointment.service_ids) && appointment.service_ids.length > 0) {
          const serviceNames = appointment.service_ids.map(s => s?.name || 'Unknown Service').join(', ');
          return `Wellness - ${serviceNames}`;
        }
        return 'Wellness Service';
      } else if (appointment?.booking_type === 'treatment') {
        return appointment?.treatment?.title || 'Prescribed Treatment';
      }
      return 'N/A';
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.02, y: -5 }}
        className="bg-white/10 backdrop-blur-lg border border-gray-600 rounded-xl p-6 hover:border-gray-500 transition-all duration-300"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white mb-1">
              {appointment?.client?.full_name || appointment?.patient?.full_name || 'Unknown Patient'}
            </h3>
            <p className="text-gray-400 text-sm">{appointment?.client?.email || appointment?.patient?.email || 'No email'}</p>
            {(appointment?.client?.phone || appointment?.patient?.phone) && (
              <p className="text-gray-400 text-sm">📱 {appointment?.client?.phone || appointment?.patient?.phone}</p>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${appointment?.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
              appointment?.status === 'confirmed' ? 'bg-blue-500/20 text-blue-400' :
                appointment?.status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-400' :
                  appointment?.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                    appointment?.status === 'scheduled' ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-500/20 text-gray-400'
              }`}>
              {appointment?.status?.replace('_', ' ')?.toUpperCase()}
            </span>
            <button
              onClick={() => setSelectedAppointment(appointment)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <MoreVertical className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Service Type:</span>
            <span className="text-white font-medium">{getBookingTypeDisplay()}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-gray-400">Date & Time:</span>
            <span className="text-white">
              {appointment?.preferred_date} at {appointment?.preferred_time}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-gray-400">Mode:</span>
            <span className="text-white capitalize">
              {appointment?.consultation_mode?.replace('_', ' ')}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-gray-400">Fee:</span>
            <span className="text-emerald-400 font-semibold">
              ₹{appointment?.fee}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-gray-400">Payment:</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${appointment?.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
              appointment?.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                appointment?.payment_status === 'refunded' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-red-500/20 text-red-400'
              }`}>
              {appointment?.payment_status || 'pending'}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-gray-400">Severity:</span>
            <span className={`capitalize ${appointment?.severity === 'severe' ? 'text-red-400' :
              appointment?.severity === 'moderate' ? 'text-yellow-400' : 'text-green-400'
              }`}>
              {appointment?.severity}
            </span>
          </div>
        </div>

        {appointment?.chief_complaint && (
          <div className="mt-4 pt-4 border-t border-gray-700">
            <p className="text-sm text-gray-300">
              <span className="text-gray-400">Complaint: </span>
              {appointment?.chief_complaint?.length > 100
                ? `${appointment?.chief_complaint?.substring(0, 100)}...`
                : appointment?.chief_complaint}
            </p>
          </div>
        )}

        <div className="flex space-x-2 mt-4">
          <button
            onClick={() => setSelectedAppointment(appointment)}
            className="flex-1 flex items-center justify-center space-x-2 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Eye className="w-4 h-4" />
            <span>View</span>
          </button>
          {(appointment?.client?.phone || appointment?.patient?.phone) && (
            <button
              onClick={() => window.open(`tel:${appointment?.client?.phone || appointment?.patient?.phone}`, '_self')}
              className="flex items-center justify-center p-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
              title="Call patient"
            >
              <Phone className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => {
              const phone = appointment?.client?.phone || appointment?.patient?.phone;
              if (phone) {
                window.open(`https://wa.me/${phone.replace(/[^0-9]/g, '')}`, '_blank');
              }
            }}
            className="flex items-center justify-center p-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            title="Send WhatsApp message"
          >
            <MessageSquare className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    );
  };

  const AppointmentModal = ({ appointment, onClose }) => {
    const [editingPayment, setEditingPayment] = useState(false);
    const [paymentData, setPaymentData] = useState({
      status: appointment?.payment_status || 'pending',
      method: appointment?.payment_method || 'clinic'
    });

    if (!appointment) return null;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          onClick={(e) => e?.stopPropagation()}
          className="bg-slate-800 border border-gray-600 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">Appointment Details</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Patient Name</label>
                <p className="text-white">{appointment?.client?.full_name || appointment?.patient?.full_name || 'Unknown'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Email</label>
                <p className="text-white">{appointment?.client?.email || appointment?.patient?.email || 'Not provided'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Phone</label>
                <p className="text-white">{appointment?.client?.phone || appointment?.patient?.phone || 'Not provided'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Doctor</label>
                <p className="text-white">
                  {appointment?.doctor?.full_name ? `Dr. ${appointment.doctor.full_name}` : 'Not assigned'}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Service/Treatment</label>
              <p className="text-white">
                {appointment?.booking_type === 'consultation' && 'Consultation'}
                {appointment?.booking_type === 'service' && (
                  <>
                    Wellness Service
                    {appointment?.service_ids && Array.isArray(appointment.service_ids) && appointment.service_ids.length > 0 && (
                      <span className="text-gray-300"> - {appointment.service_ids.map(s => s.name).join(', ')}</span>
                    )}
                  </>
                )}
                {appointment?.booking_type === 'treatment' && (appointment?.treatment?.title || 'Prescribed Treatment')}
                {!appointment?.booking_type && (appointment?.treatment?.title || 'N/A')}
              </p>
              {appointment?.treatment?.short_description && (
                <p className="text-gray-400 text-sm mt-1">{appointment?.treatment?.short_description}</p>
              )}
              {appointment?.booking_type === 'service' && appointment?.service_ids && Array.isArray(appointment.service_ids) && appointment.service_ids.length > 0 && (
                <div className="mt-2 space-y-1">
                  {appointment.service_ids.map((service, index) => (
                    <div key={index} className="text-sm text-gray-400 flex justify-between">
                      <span>• {service.name}</span>
                      <span>₹{service.base_price}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Appointment Date</label>
                <p className="text-white">{appointment?.preferred_date}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Appointment Time</label>
                <p className="text-white">{appointment?.preferred_time}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Chief Complaint</label>
              <p className="text-white bg-white/5 p-3 rounded-lg">{appointment?.chief_complaint}</p>
            </div>

            {appointment?.medical_history && (
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Medical History</label>
                <p className="text-white bg-white/5 p-3 rounded-lg">{appointment?.medical_history}</p>
              </div>
            )}

            {/* Payment Information Section */}
            <div className="pt-4 border-t border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center">
                  <DollarSign className="w-5 h-5 mr-2 text-emerald-400" />
                  Payment Information
                </h3>
                {!editingPayment && (
                  <button
                    onClick={() => setEditingPayment(true)}
                    className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Edit Payment
                  </button>
                )}
              </div>

              {editingPayment ? (
                <div className="space-y-4 bg-white/5 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Payment Status</label>
                      <select
                        value={paymentData?.status}
                        onChange={(e) => setPaymentData(prev => ({ ...prev, status: e?.target?.value }))}
                        className="w-full px-3 py-2 bg-white/10 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="refunded">Refunded</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Payment Method</label>
                      <select
                        value={paymentData?.method}
                        onChange={(e) => setPaymentData(prev => ({ ...prev, method: e?.target?.value }))}
                        className="w-full px-3 py-2 bg-white/10 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="clinic">Pay at Clinic</option>
                        <option value="online">Online Payment</option>
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="upi">UPI</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      onClick={() => {
                        setEditingPayment(false);
                        setPaymentData({
                          status: appointment?.payment_status || 'pending',
                          method: appointment?.payment_method || 'clinic'
                        });
                      }}
                      className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        updatePaymentStatus(appointment?.id, paymentData?.status, paymentData?.method);
                        setEditingPayment(false);
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      Save Payment
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4 bg-white/5 p-4 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Fee Amount</label>
                    <p className="text-white text-lg font-semibold">₹{appointment?.fee || 0}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Payment Status</label>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${appointment?.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                      appointment?.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        appointment?.payment_status === 'refunded' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-red-500/20 text-red-400'
                      }`}>
                      {appointment?.payment_status || 'pending'}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Payment Method</label>
                    <p className="text-white capitalize">{appointment?.payment_method || 'Not specified'}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-700">
              <button
                onClick={() => generateAppointmentReceipt(appointment)}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              >
                <FileText className="w-4 h-4" />
                <span>Download Receipt</span>
              </button>
              <button
                onClick={() => updateAppointmentStatus(appointment?.id, 'confirmed')}
                disabled={appointment?.status === 'confirmed'}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg transition-colors"
              >
                Confirm
              </button>
              <button
                onClick={() => updateAppointmentStatus(appointment?.id, 'in_progress')}
                disabled={appointment?.status === 'in_progress'}
                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 text-white rounded-lg transition-colors"
              >
                Start
              </button>
              <button
                onClick={() => updateAppointmentStatus(appointment?.id, 'completed')}
                disabled={appointment?.status === 'completed'}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg transition-colors"
              >
                Complete
              </button>
              <button
                onClick={() => updateAppointmentStatus(appointment?.id, 'cancelled')}
                disabled={appointment?.status === 'cancelled'}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-emerald-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-emerald-900">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%227%22%20cy%3D%227%22%20r%3D%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-30" />
      <div className="relative z-10 container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <motion.h1
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-4"
          >
            Admin Appointment Dashboard
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-gray-300 max-w-2xl mx-auto"
          >
            Comprehensive 3D dashboard with real-time analytics and management tools
          </motion.p>
        </div>

        {Object.keys(errors)?.length > 0 && (
          <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-lg p-4">
            {Object.values(errors)?.map((error, index) => (
              <p key={index} className="text-red-400">{error}</p>
            ))}
          </div>
        )}

        <DashboardMetrics />
        <FilterControls />

        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-white">
              Appointments ({filteredAppointments?.length})
            </h2>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowBookingModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Create Booking</span>
              </button>
              <button className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors">
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          </div>

          {filteredAppointments?.length === 0 ? (
            <div className="bg-white/10 backdrop-blur-lg border border-gray-600 rounded-xl p-12 text-center">
              <Calendar className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No appointments found matching your criteria</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAppointments?.map((appointment) => (
                <AppointmentCard
                  key={appointment?.id}
                  appointment={appointment}
                />
              ))}
            </div>
          )}
        </div>

        <AnimatePresence>
          {selectedAppointment && (
            <AppointmentModal
              appointment={selectedAppointment}
              onClose={() => setSelectedAppointment(null)}
            />
          )}
        </AnimatePresence>

        {/* Admin Booking Creation Modal */}
        <AdminBookingModal
          isOpen={showBookingModal}
          onClose={() => setShowBookingModal(false)}
          onSuccess={() => {
            setShowBookingModal(false);
            loadDashboardData();
          }}
        />
      </div>
    </div>
  );
};

export default AdminAppointmentDashboard;